import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  Package, Handshake, Warning, TrayArrowDown, CalendarCheck,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { DashboardZone, ActionTile, ListCard, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { bihinApi } from './bihinApi';

/**
 * 備品ダッシュボード — decision-driven (Jun 2026).
 * Removed by the Decision Value Test: 備品総数 / 消耗品総数 / 貸出中 stat cards
 * (inventory counts with no action attached) — they now live behind 参考指標.
 */
export default function BihinDashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { bihinApi.dashboard().then(setD).catch(() => {}); }, []);

  if (!d) return <div className="py-16 text-center text-zinc-400 text-sm">読み込み中…</div>;

  const overdueInsp = d.inspection_overdue || [];
  const dueSoonInsp = d.inspection_due_soon || [];
  const inspAll = [...overdueInsp, ...dueSoonInsp];
  const lowStock = d.low_stock || [];
  const overdueLoans = d.overdue_loans || [];
  const nothingToDo = overdueInsp.length === 0 && dueSoonInsp.length === 0 && lowStock.length === 0 && overdueLoans.length === 0;

  return (
    <div className="space-y-8" data-testid="bihin-dashboard">
      <PageHeader
        testid="bihin-dashboard-header"
        title="備品ダッシュボード"
        description="対応が必要な点検・在庫・返却をまとめて確認できます"
      />

      <DashboardZone title="対応が必要" hint="クリックすると該当画面に移動します" testid="bihin-action-zone">
        {nothingToDo ? (
          <AllClear message="点検・在庫・返却で対応が必要な項目はありません" testid="bihin-allclear" />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionTile icon={Warning} label="点検期限切れ" hint="期限を過ぎた点検" value={overdueInsp.length} urgent
              onClick={() => navigate('/bihin/items')} testid="bihin-stat-inspections" />
            <ActionTile icon={Handshake} label="返却期限超過" hint="未返却の貸出" value={overdueLoans.length} urgent
              onClick={() => navigate('/bihin/loans')} testid="bihin-stat-overdue-loans" />
            <ActionTile icon={TrayArrowDown} label="在庫不足" hint="最低在庫を下回る消耗品" value={lowStock.length}
              onClick={() => navigate('/bihin/items')} testid="bihin-stat-lowstock" />
            <ActionTile icon={CalendarCheck} label="点検間近" hint="期限が近い点検" value={dueSoonInsp.length}
              onClick={() => navigate('/bihin/items')} testid="bihin-stat-insp-soon" />
          </div>
        )}
      </DashboardZone>

      {overdueLoans.length > 0 && (
        <ListCard title="返却期限を過ぎた貸出" icon={Handshake} count={overdueLoans.length} testid="bihin-dash-overdue-loans">
          <ul className="divide-y divide-zinc-200">
            {overdueLoans.map((l) => (
              <li key={l.id}>
                <Link to={`/bihin/items?open=${l.item_id}`}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-teal-50/50 transition-colors"
                  data-testid={`bihin-dash-overdue-${l.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-800 truncate"><span data-no-translate>{l.item_name}</span></div>
                    <div className="text-[11px] text-zinc-500">{l.borrower_name}</div>
                  </div>
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">期限 {l.due_date}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        </ListCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ListCard title="点検予定・期限切れ" icon={CalendarCheck} count={inspAll.length} testid="bihin-dash-inspections">
          {inspAll.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400">点検予定はありません</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {inspAll.slice(0, 10).map((it) => {
                const overdue = overdueInsp.some((x) => x.id === it.id);
                return (
                  <li key={it.id}>
                    <Link to={`/bihin/items?open=${it.id}`}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-teal-50/50 transition-colors"
                      data-testid={`bihin-dash-insp-${it.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-800 truncate">{it.name}</div>
                        {it.asset_no && <div className="text-[10px] font-mono text-zinc-400">{it.asset_no}</div>}
                      </div>
                      <Badge variant="outline" className={overdue ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                        {it.next_inspection_date} {overdue ? '期限切れ' : '間近'}
                      </Badge>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </ListCard>

        <ListCard title="在庫不足の消耗品" icon={Warning} count={lowStock.length} testid="bihin-dash-lowstock">
          {lowStock.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400">在庫不足はありません</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {lowStock.slice(0, 10).map((it) => (
                <li key={it.id}>
                  <Link to={`/bihin/items?open=${it.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-teal-50/50 transition-colors"
                    data-testid={`bihin-dash-lowstock-${it.id}`}>
                    <div className="text-sm font-medium text-zinc-800 truncate">{it.name}</div>
                    <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                      残 {it.stock_qty}{it.unit || ''} / 最低 {it.min_qty}{it.unit || ''}
                    </Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      </div>

      <MetricsDisclosure title="参考指標" testid="bihin-metrics">
        <div className="grid grid-cols-3 gap-3 pt-2">
          {[
            { label: '備品', value: d.total_equipment, Icon: Package },
            { label: '消耗品', value: d.total_consumables, Icon: TrayArrowDown },
            { label: '貸出中', value: d.active_loans, Icon: Handshake },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border border-zinc-200 p-3">
              <p className="flex items-center gap-1.5 text-[11px] text-zinc-500"><k.Icon size={13} weight="duotone" className="text-zinc-400" />{k.label}</p>
              <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{k.value ?? 0}</p>
            </div>
          ))}
        </div>
      </MetricsDisclosure>
    </div>
  );
}
